import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const NavDropdown = ({ id, label, items, align = "left", compact = false, mega = false }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const btn = useRef(null);
  const closeTimer = useRef(null);
  const hoverAt = useRef(0);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  const menuItems = () => [...wrap.current.querySelectorAll('[role="menuitem"]')];
  const focusAt = (i) => {
    const els = menuItems();
    if (els.length) els[(i + els.length) % els.length].focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape" && open) {
      setOpen(false);
      btn.current.focus();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const dir = e.key === "ArrowDown" ? 1 : -1;
      if (!open) {
        setOpen(true);
        requestAnimationFrame(() => focusAt(dir === 1 ? 0 : -1));
      } else {
        focusAt(menuItems().indexOf(document.activeElement) + dir);
      }
    }
  };

  const onBlur = (e) => {
    if (!wrap.current.contains(e.relatedTarget)) setOpen(false);
  };

  const active = items.some((it) => it.to && (pathname === it.to || pathname.startsWith(`${it.to}/`)));

  return (
    <div
      ref={wrap}
      className="relative"
      onMouseEnter={() => {
        clearTimeout(closeTimer.current);
        if (!open) hoverAt.current = Date.now();
        setOpen(true);
      }}
      onMouseLeave={() => (closeTimer.current = setTimeout(() => setOpen(false), 160))}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      <button
        ref={btn}
        type="button"
        data-testid={`nav-${id}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={`menu-${id}`}
        onClick={() => setOpen((o) => (Date.now() - hoverAt.current < 450 ? true : !o))}
        className={`group flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[14.5px] font-medium transition-colors ${
          active || open ? "text-gold" : "text-paper/90 hover:text-gold"
        } ${compact ? "font-mono text-[12px] uppercase tracking-[0.14em]" : ""}`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
        {open && (
          <div
            id={`menu-${id}`}
            role="menu"
            aria-label={label}
            data-testid={`nav-${id}-menu`}
            className={`menu-in absolute top-full z-50 mt-2 overflow-hidden rounded-2xl border border-gold/15 bg-[#061e33]/95 p-2 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] backdrop-blur-xl ${
              mega ? "-left-48 grid w-[680px] grid-cols-2 gap-x-1" : "w-[300px]"
            } ${!mega && align === "right" ? "right-0" : !mega ? "left-0" : ""}`}
          >
            {items.map((it, idx) => {
              const tid = `nav-${id}-${slug(it.label)}`;
              const inner = (
                <>
                  <span className="flex items-center gap-2 text-[15px] font-medium text-paper group-hover:text-gold group-focus-visible:text-gold">
                    {it.urgent && <span className="h-1.5 w-1.5 rounded-full bg-alert" aria-hidden="true" />}
                    {it.label}
                    {it.href && <ArrowUpRight className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />}
                  </span>
                  {it.desc && <span className="mt-0.5 block text-[13px] text-mist">{it.desc}</span>}
                </>
              );
              const cls = `group block rounded-xl px-4 py-3 transition-colors hover:bg-gold/[0.07] focus-visible:bg-gold/[0.07] focus-visible:outline-none ${mega && idx === 0 ? "col-span-2 mb-1 border-b border-gold/10 pb-4" : ""}`;
              return it.href ? (
                <a key={it.label} role="menuitem" href={it.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={tid}>
                  {inner}
                </a>
              ) : (
                <Link key={it.label} role="menuitem" to={it.to} className={cls} data-testid={tid}>
                  {inner}
                </Link>
              );
            })}
          </div>
        )}
    </div>
  );
};
